import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { CheckCircle2, ArrowRight, ClipboardList, Smartphone, FileText, Download } from 'lucide-react';
import { iceLogo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { startCheckout } from '../lib/api';
import PaymentSection from '../components/PaymentSection';

const divisions = [
  { id: 'u7', label: 'U7 Football', desc: 'Ages 5-7' },
  { id: 'u9', label: 'U9 Football', desc: 'Ages 7-9' },
  { id: 'u11', label: 'U11 Football', desc: 'Ages 9-11' },
  { id: 'u13', label: 'U13 Football', desc: 'Ages 11-13' },
  { id: 'u5-football', label: 'U5 Football', desc: 'Ages 5' },
  { id: 'varsity-cheer', label: 'Varsity Cheer', desc: 'Ages 11-14' }
];

// Season documents — replace href with the real PDF URLs when uploaded
const documents = [
  {
    id: 'registration-packet-2026',
    title: 'BJP 2026 Registration Packet',
    desc: 'Full season registration & scholar application',
    href: 'https://brooklinejppatriots.com/docs/bjp-2026-registration-packet.pdf'
  },
  {
    id: 'cori-form-2026',
    title: 'BJP 2026 CORI Form',
    desc: 'Volunteer background check — required for coaches & parent helpers',
    href: 'https://brooklinejppatriots.com/docs/bjp-2026-cori-form.pdf'
  }
];

export default function Register() {
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const [paying, setPaying] = useState(false);
  const preselected = params.get('div') || '';

  const [form, setForm] = useState({
    scholar: '',
    dob: '',
    grade: '',
    division: preselected,
    parent: '',
    phone: '',
    email: '',
    address: '',
    emergency: '',
    notes: '',
    agree: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'division') {
      setParams({ div: value }, { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.scholar || !form.division || !form.parent || !form.phone) {
      toast({ title: 'Missing info', description: 'Scholar name, division, parent name & phone are required.' });
      return;
    }
    if (!form.agree) {
      toast({ title: 'Please accept', description: 'You must agree to the code of conduct.' });
      return;
    }
    if (!form.email) {
      toast({ title: 'Email required', description: 'We need a parent email for your Stripe receipt.' });
      return;
    }
    setPaying(true);
    try {
      const { agree, ...registration } = form;
      const { checkout_url } = await startCheckout({ kind: 'registration', registration });
      window.location.assign(checkout_url);
    } catch (err) {
      toast({ title: 'Could not start checkout', description: err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || err.message });
      setPaying(false);
    }
  };

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-20 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-4 mb-4">
            <img src={iceLogo} alt="ICE Logo" className="h-14 w-auto object-contain" data-testid="register-hero-logo" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">Season Registration</div>
          </div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none" data-testid="register-title">
            REGISTER <span className="text-[#c8102e]">NOW</span>
          </h1>
          <p className="text-white/75 max-w-2xl mt-6 text-lg">
            Fill out the application below. A coach will contact you within 48 hours to finalize enrollment.
          </p>
        </div>
      </section>

      {/* Season document QR codes */}
      <section className="py-20 bg-white" id="scan-apply">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Smartphone className="w-10 h-10 text-[#c8102e] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Scan &amp; Apply</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">2026 SEASON FORMS</h2>
            <p className="text-[#0a1f44]/70 mt-4">Scan the QR code with your phone camera to download and fill out each form. You can also tap the button below each code.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-testid="qr-docs-grid">
            {documents.map((doc) => (
              <div key={doc.id} data-testid={`qr-${doc.id}`} className="hover-lift border-2 border-[#0a1f44]/10 bg-white p-8 text-center flex flex-col items-center">
                <div className="hidden sm:inline-block bg-white p-3 border-2 border-[#0a1f44] mb-5">
                  <QRCodeCanvas value={doc.href} size={180} bgColor="#ffffff" fgColor="#0a1f44" level="M" includeMargin={false} />
                </div>
                <FileText className="w-6 h-6 text-[#c8102e] mb-2" />
                <div className="font-display text-[#0a1f44] text-2xl leading-tight">{doc.title.toUpperCase()}</div>
                <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60 mt-2 mb-4">{doc.desc}</div>
                <a
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`download-${doc.id}`}
                  className="mt-auto inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-3 transition-colors"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-[#0a1f44]/60 text-xs mt-8 max-w-xl mx-auto">
            Print the QR codes for open-house flyers, practice-field posters, and community-center bulletin boards.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-[#f5f6f8]" id="apply">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <ClipboardList className="w-10 h-10 text-[#c8102e] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Fill Below</div>
            <h2 className="font-display text-[#0a1f44] text-5xl">SCHOLAR APPLICATION</h2>
          </div>

          <form onSubmit={handleSubmit} className="bg-white shadow-sm p-4 sm:p-8 space-y-6" data-testid="registration-form">
            <div>
              <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60 mb-3">Scholar Info</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full Name *" name="scholar" value={form.scholar} onChange={handleChange} required />
                <Field label="Date of Birth" name="dob" type="date" value={form.dob} onChange={handleChange} />
                <Field label="Grade / School" name="grade" value={form.grade} onChange={handleChange} />
                <div>
                  <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">Division *</label>
                  <select
                    name="division"
                    value={form.division}
                    onChange={handleChange}
                    required
                    data-testid="form-division"
                    className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e] bg-white"
                  >
                    <option value="">Select division…</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>{`${d.label} — ${d.desc}`}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60 mb-3 mt-2">Parent / Guardian</div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Parent Name *" name="parent" value={form.parent} onChange={handleChange} required />
                <Field label="Phone *" name="phone" type="tel" value={form.phone} onChange={handleChange} required />
                <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} />
                <Field label="Home Address" name="address" value={form.address} onChange={handleChange} />
                <Field label="Emergency Contact" name="emergency" value={form.emergency} onChange={handleChange} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">Notes / Medical Info</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
                data-testid="form-notes"
                className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
                placeholder="Allergies, injuries, or anything the coaches should know"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="agree"
                checked={form.agree}
                onChange={handleChange}
                data-testid="form-agree"
                className="mt-1 accent-[#c8102e] w-4 h-4"
              />
              <span className="text-sm text-[#0a1f44]/80 leading-relaxed">
                I agree to the &quot;ICE&quot; Brookline JP Patriots Code of Conduct and confirm the information above is accurate. A coach will contact me within 48 hours to complete enrollment.
              </span>
            </label>

            <button
              type="submit"
              disabled={paying}
              data-testid="form-submit"
              className="w-full group inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-60 text-white font-heading uppercase tracking-widest px-10 py-4 transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" /> {paying ? 'Opening secure checkout…' : 'Register & Pay $175 with Stripe'} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </section>
      <PaymentSection variant="both" />
    </div>
  );
}

function Field({ label, name, value, onChange, type = 'text', required = false }) {
  const autoComplete = { scholar: 'name', parent: 'name', phone: 'tel', email: 'email', address: 'street-address' }[name];
  const inputMode = type === 'email' ? 'email' : type === 'tel' ? 'tel' : undefined;
  return (
    <div>
      <label className="block text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70 mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        data-testid={`form-${name}`}
        className="w-full border border-[#0a1f44]/20 px-4 py-3 focus:outline-none focus:border-[#c8102e]"
      />
    </div>
  );
}
